import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  workItemInterviewApi,
  type SaveInterviewDraftInput,
  type SubmitInterviewInput,
} from "@/lib/api/work-item-interview";
import { workItemKeys } from "./use-work-items";

/**
 * Query key factory for the Work-Item Formulator interview (PI-SPEC-SW-1364;
 * Toolbox Formulator PI-SPEC-SW-1418). `detail` is the session itself (the
 * key a `turn-finished` event invalidates); `active` is the resume list.
 */
export const workItemInterviewKeys = {
  all: ["work-item-interview"] as const,
  active: () => [...workItemInterviewKeys.all, "active"] as const,
  session: (sessionId: string) => [...workItemInterviewKeys.all, "session", sessionId] as const,
  detail: (sessionId: string) => workItemInterviewKeys.session(sessionId),
  review: (sessionId: string) => [...workItemInterviewKeys.all, "review", sessionId] as const,
};

/**
 * The caller's unfinished sessions ("Pick up where you left off"): active and
 * unexpired, newest first, at most 20.
 */
export function useActiveWorkItemInterviews(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: workItemInterviewKeys.active(),
    queryFn: () => workItemInterviewApi.listActive(),
    select: (response) => response.data,
    enabled: options.enabled ?? true,
    retry: false,
  });
}

/** The interview as it stands; used to resume. Disabled until a session exists. */
export function useWorkItemInterview(sessionId: string | null) {
  return useQuery({
    queryKey: workItemInterviewKeys.session(sessionId ?? ""),
    queryFn: () => workItemInterviewApi.get(sessionId ?? ""),
    select: (response) => response.data,
    enabled: sessionId !== null,
    retry: false,
  });
}

/**
 * The before/after diff for approval. Only fetched when `sessionId` is set
 * (the review phase). Always refetched on mount so the user approves the
 * diff the server would write now, never a stale copy.
 */
export function useWorkItemInterviewReview(sessionId: string | null) {
  return useQuery({
    queryKey: workItemInterviewKeys.review(sessionId ?? ""),
    queryFn: () => workItemInterviewApi.review(sessionId ?? ""),
    select: (response) => response.data,
    enabled: sessionId !== null,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
}

/**
 * Write the approved diff. The only call in the Formulator that writes to the
 * tracker; it is reachable only from the diff view's Approve button. On
 * success every work-item query is invalidated so My Items and the item page
 * show the new readiness.
 */
export function useSubmitWorkItemInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitInterviewInput) => workItemInterviewApi.submit(input),
    onSuccess: (_response, input) => {
      void queryClient.invalidateQueries({ queryKey: workItemKeys.all });
      void queryClient.invalidateQueries({ queryKey: workItemInterviewKeys.active() });
      queryClient.removeQueries({ queryKey: workItemInterviewKeys.review(input.sessionId) });
    },
  });
}

/**
 * Save the person's edit of the draft (epic PI-SPEC-SW-E321). The response
 * is the new review, so it replaces the cached review at once (its
 * `diffHash` follows the edit) and the session is refetched.
 */
export function useSaveWorkItemInterviewDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveInterviewDraftInput) => workItemInterviewApi.saveDraft(input),
    onSuccess: (response, input) => {
      queryClient.setQueryData(workItemInterviewKeys.review(input.sessionId), response);
      void queryClient.invalidateQueries({ queryKey: workItemInterviewKeys.review(input.sessionId) });
      void queryClient.invalidateQueries({ queryKey: workItemInterviewKeys.detail(input.sessionId) });
    },
  });
}
